// DevSecOps website article topics - separate from Filesig topics
// Path: admin/settings/devsecopsTopics
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const ADMIN_SETTINGS_DOC = 'admin/settings'
const DEVSECOPS_TOPICS_COLLECTION = 'devsecopsTopics'

export const createDevSecOpsTopic = async (topicText, categoryId = null) => {
  try {
    const trimmedText = topicText.trim()
    const normalizedText = trimmedText.toLowerCase()

    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, DEVSECOPS_TOPICS_COLLECTION)
    const existingTopicsQuery = query(topicsRef)
    const existingTopicsSnapshot = await getDocs(existingTopicsQuery)

    const duplicate = existingTopicsSnapshot.docs.find(doc => {
      const data = doc.data()
      if (!data.text) return false
      const existingNormalized = data.text.trim().toLowerCase()
      const textMatches = existingNormalized === normalizedText
      const existingCategory = data.category || null
      const categoryMatches = existingCategory === categoryId
      return textMatches && categoryMatches
    })

    if (duplicate) {
      throw new Error('A topic with this text and category already exists')
    }

    const normalizedCategory = (categoryId && categoryId.trim()) ? categoryId.trim() : null

    const topicData = {
      text: trimmedText,
      status: 'unprocessed',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      category: normalizedCategory
    }

    const docRef = await addDoc(topicsRef, topicData)
    return docRef.id
  } catch (error) {
    console.error('Error creating DevSecOps topic:', error)
    throw error
  }
}

export const updateDevSecOpsTopic = async (topicId, updates) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, DEVSECOPS_TOPICS_COLLECTION)
    const docRef = doc(topicsRef, topicId)

    const currentTopicSnap = await getDoc(docRef)
    const currentTopicData = currentTopicSnap.exists() ? currentTopicSnap.data() : {}

    const newText = updates.text !== undefined ? updates.text.trim() : currentTopicData.text
    const newCategory = updates.category !== undefined
      ? (updates.category === null || updates.category === '' ? null : updates.category)
      : (currentTopicData.category || null)

    const normalizedText = newText ? newText.toLowerCase() : ''

    if (updates.text !== undefined || updates.category !== undefined) {
      const existingTopicsQuery = query(topicsRef)
      const existingTopicsSnapshot = await getDocs(existingTopicsQuery)

      const duplicate = existingTopicsSnapshot.docs.find(doc => {
        if (doc.id === topicId) return false
        const data = doc.data()
        if (!data.text) return false
        const existingNormalized = data.text.trim().toLowerCase()
        const textMatches = existingNormalized === normalizedText
        const existingCategory = data.category || null
        const categoryMatches = existingCategory === newCategory
        return textMatches && categoryMatches
      })

      if (duplicate) {
        throw new Error('A topic with this text and category already exists')
      }
    }

    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    }

    if (updates.text !== undefined) {
      updateData.text = updates.text.trim()
    }

    if (updates.category !== undefined) {
      if (updates.category === null || updates.category === '') {
        updateData.category = null
      } else {
        updateData.category = updates.category
      }
    }

    await updateDoc(docRef, updateData)
  } catch (error) {
    console.error('Error updating DevSecOps topic:', error)
    throw error
  }
}

export const deleteDevSecOpsTopic = async (topicId) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, DEVSECOPS_TOPICS_COLLECTION)
    const docRef = doc(topicsRef, topicId)
    await deleteDoc(docRef)
  } catch (error) {
    console.error('Error deleting DevSecOps topic:', error)
    throw error
  }
}

export const subscribeToDevSecOpsTopics = (callback, includeUsed = true) => {
  const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
  const topicsRef = collection(adminSettingsRef, DEVSECOPS_TOPICS_COLLECTION)

  let q
  if (includeUsed) {
    q = query(topicsRef, orderBy('createdAt', 'desc'))
  } else {
    q = query(
      topicsRef,
      where('status', 'in', ['unprocessed', 'available']),
      orderBy('createdAt', 'desc')
    )
  }

  return onSnapshot(q, (snapshot) => {
    const topics = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(topics)
  }, (error) => {
    console.error('Error subscribing to DevSecOps topics:', error)
  })
}

export const markDevSecOpsTopicAsUsed = async (topicId) => {
  try {
    await updateDevSecOpsTopic(topicId, { status: 'used' })
  } catch (error) {
    console.error('Error marking DevSecOps topic as used:', error)
    throw error
  }
}

export const markDevSecOpsTopicAsUnused = async (topicId) => {
  try {
    await updateDevSecOpsTopic(topicId, { status: 'available' })
  } catch (error) {
    console.error('Error marking DevSecOps topic as available:', error)
    throw error
  }
}
